import { useState, useEffect } from "react";
import { collection, getDocs, setDoc, doc } from "firebase/firestore";
import { db } from "../../../firebase/config";
import { Athlete, Entry } from "../../../types";
import { parseEventScore, formatEventScore } from "../../../utils/scoreUtils";

const EVENTS = ["bench", "squat", "shots", "swim", "run"];

export default function EntryForm() {
  const [athletes, setAthletes] = useState<Athlete[]>([]);
  const [formData, setFormData] = useState<Partial<Entry> & { date?: string }>({
    event: "",
    result: "",
    date: "",
  });

  useEffect(() => {
    const fetchAthletes = async () => {
      const snapshot = await getDocs(collection(db, "athletes"));
      const data = snapshot.docs.map((doc) => doc.data() as Athlete);
      setAthletes(data);
    };
    fetchAthletes();
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.email || !formData.event || !formData.result) return;

    const athlete = athletes.find((a) => a.email === formData.email);
    if (!athlete) return;

    const date = new Date(formData.date || new Date().toISOString());
    const score = parseEventScore(formData.event, formData.result.toString());

    const entry: Entry = {
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      day: date.getDate(),
      age: new Date().getFullYear() - new Date(athlete.birthday).getFullYear(),
      event: formData.event,
      firstName: athlete.firstName,
      lastName: athlete.lastName,
      gender: athlete.gender,
      email: athlete.email,
      result: formData.result.toString(),
      score,
    };

    await setDoc(doc(collection(db, "entries")), entry);
    alert("Entry saved!");
    setFormData({ event: "", result: "" });
  };

  const handleAddAll = async () => {
    const response = await fetch("/backup-entries.json");
    const data = await response.json();

    for (const item of data) {
      const score =
        typeof item.result === "number" ? item.result : parseFloat(item.result);
      const event = item.event ? item.event.toLowerCase() : "";
      const adjustedScore = score / 1000;
      const readableResult =
        event === "run" || event === "swim"
          ? formatEventScore(event, adjustedScore)
          : score.toString();

      const entry: Entry = {
        year: item.year,
        month: item.month,
        day: item.day,
        age: item.age,
        event,
        firstName: item.firstName,
        lastName: item.lastName,
        gender: item.gender,
        email: item.email,
        result: readableResult,
        score: adjustedScore,
      };

      await setDoc(doc(collection(db, "entries")), entry);
    }

    alert("All backup entries uploaded!");
  };

  const handleFixScores = async () => {
    const snapshot = await getDocs(collection(db, "entries"));
    const entries = snapshot.docs;

    for (const docSnap of entries) {
      const entry = docSnap.data() as Entry;

      if (["bench", "squat", "shots"].includes(entry.event)) {
        const correctedScore = entry.score * 1000;

        await setDoc(doc(db, "entries", docSnap.id), {
          ...entry,
          score: correctedScore,
          result: correctedScore.toString(),
        });
      }
    }

    alert("Bench, Squat, and Shots entries corrected!");
  };

  return (
    <form className="admin-form" onSubmit={handleSubmit}>
      <select
        name="email"
        value={formData.email || ""}
        onChange={handleChange}
        required
      >
        <option value="">Select Athlete</option>
        {athletes.map((a) => (
          <option key={a.email} value={a.email}>
            {a.firstName} {a.lastName}
          </option>
        ))}
      </select>

      <select
        name="event"
        value={formData.event || ""}
        onChange={handleChange}
        required
      >
        <option value="">Select Event</option>
        {EVENTS.map((event) => (
          <option key={event} value={event}>
            {event}
          </option>
        ))}
      </select>

      <input
        type="date"
        name="date"
        value={formData.date || ""}
        onChange={handleChange}
        required
      />
      <input
        name="result"
        placeholder="Result"
        value={formData.result || ""}
        onChange={handleChange}
        required
      />

      <button type="submit">Save Entry</button>
      <button type="button" onClick={handleAddAll}>
        Add All
      </button>
      <button type="button" onClick={handleFixScores}>
        Fix Bench, Squat, and Shots Entries
      </button>
    </form>
  );
}
